import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { TipMenuItemSummary } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

type DraftItem = {
  label: string;
  amountCoins: string;
  isQuickButton: boolean;
  requiresAction: boolean;
};

const emptyDraft = (): DraftItem => ({
  label: '',
  amountCoins: '25',
  isQuickButton: true,
  requiresAction: true,
});

export function ModelTipMenuCard(): ReactElement {
  const { api } = useAuth();
  const [items, setItems] = useState<TipMenuItemSummary[]>([]);
  const [draft, setDraft] = useState<DraftItem>(emptyDraft);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const result = await api.listMyTipMenu();
    setItems(result.items);
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.studio.tipMenu.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  async function handleAdd(): Promise<void> {
    const amount = Number.parseInt(draft.amountCoins, 10);
    if (!draft.label.trim() || !Number.isFinite(amount) || amount < 1) {
      setError(t('common.studio.tipMenu.invalidItem'));
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      await api.createTipMenuItem({
        label: draft.label.trim(),
        amountCoins: amount,
        isQuickButton: draft.isQuickButton,
        requiresAction: draft.requiresAction,
      });
      setDraft(emptyDraft());
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.tipMenu.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleQuick(item: TipMenuItemSummary): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.updateTipMenuItem(item.id, { isQuickButton: !item.isQuickButton });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.tipMenu.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleRequiresAction(item: TipMenuItemSummary): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.updateTipMenuItem(item.id, { requiresAction: !item.requiresAction });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.tipMenu.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleEnabled(item: TipMenuItemSummary): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.updateTipMenuItem(item.id, { enabled: !item.enabled });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.tipMenu.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(itemId: string): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.deleteTipMenuItem(itemId);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.tipMenu.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.tipMenu.loading')}</p>;
  }

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.tipMenu.title')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.tipMenu.hint')}
      </p>

      {error ? <p className="fm-error">{error}</p> : null}

      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem' }}>
        {items.map((item) => (
          <li
            key={item.id}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              alignItems: 'center',
              padding: '0.5rem 0',
              borderBottom: '1px solid var(--fm-color-border)',
              opacity: item.enabled ? 1 : 0.55,
            }}
          >
            <span style={{ flex: '1 1 10rem', fontWeight: 600 }}>
              {item.label} · {item.amountCoins}
            </span>
            <label style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', fontSize: '0.8125rem' }}>
              <input
                type="checkbox"
                checked={item.requiresAction}
                disabled={isSaving}
                onChange={() => {
                  void handleToggleRequiresAction(item);
                }}
              />
              {t('common.studio.tipMenu.requiresAction')}
            </label>
            <label style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', fontSize: '0.8125rem' }}>
              <input
                type="checkbox"
                checked={item.isQuickButton}
                disabled={isSaving}
                onChange={() => {
                  void handleToggleQuick(item);
                }}
              />
              {t('common.studio.tipMenu.quick')}
            </label>
            <label style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', fontSize: '0.8125rem' }}>
              <input
                type="checkbox"
                checked={item.enabled}
                disabled={isSaving}
                onChange={() => {
                  void handleToggleEnabled(item);
                }}
              />
              {t('common.studio.tipMenu.enabled')}
            </label>
            <Button
              label={t('common.studio.tipMenu.delete')}
              variant="ghost"
              disabled={isSaving}
              onClick={() => {
                void handleDelete(item.id);
              }}
            />
          </li>
        ))}
      </ul>

      {items.length === 0 ? (
        <p style={{ color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
          {t('common.studio.tipMenu.empty')}
        </p>
      ) : null}

      <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem' }}>
        <label>
          {t('common.studio.tipMenu.label')}
          <input
            value={draft.label}
            onChange={(event) => {
              setDraft({ ...draft, label: event.target.value });
            }}
            placeholder={t('common.studio.tipMenu.labelPlaceholder')}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label>
          {t('common.studio.tipMenu.amount')}
          <input
            type="number"
            min={1}
            value={draft.amountCoins}
            onChange={(event) => {
              setDraft({ ...draft, amountCoins: event.target.value });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={draft.requiresAction}
            onChange={(event) => {
              setDraft({ ...draft, requiresAction: event.target.checked });
            }}
          />
          {t('common.studio.tipMenu.requiresAction')}
        </label>
        <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--fm-color-text-muted)' }}>
          {t('common.studio.tipMenu.requiresActionHint')}
        </p>
        <label style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={draft.isQuickButton}
            onChange={(event) => {
              setDraft({ ...draft, isQuickButton: event.target.checked });
            }}
          />
          {t('common.studio.tipMenu.quick')}
        </label>
        <Button
          label={isSaving ? t('common.studio.tipMenu.saving') : t('common.studio.tipMenu.add')}
          disabled={isSaving}
          onClick={() => {
            void handleAdd();
          }}
        />
      </div>
    </div>
  );
}
